import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Calendar,
  BarChart3,
  PieChart,
  Activity,
  Percent,
  ShoppingCart,
  Trash2,
  AlertCircle,
  CheckCircle,
  Info,
  ChefHat
} from 'lucide-react';
import {
  useDashboardOverview,
  useFinancialMetrics,
  useWasteMetrics,
  useInventoryMetrics,
  useMaterialUsage,
  useRecipeCostAnalysis,
  useMaterials
} from '../hooks/useInventory';
import { useInventoryRealtime } from '../hooks/useInventoryRealtime';
import type { AnalyticsDateRange, FinancialMetrics, WasteMetrics } from '../types';
import AdvancedAnalyticsTables from '../components/AdvancedAnalyticsTables';

/**
 * Unified Inventory Analytics Page
 * 
 * Replaces:
 * - InventoryAnalytics.tsx
 * - CostAnalysis.tsx
 * - AdvancedCostAnalytics.tsx
 * - BusinessInsightsDashboard.tsx
 * - AdvancedAnalyticsDashboard.tsx
 * 
 * Features:
 * - Single source of truth using AnalyticsService
 * - Clean, readable UI with proper spacing
 * - Full Arabic translation support
 * - Responsive design
 * - Clear data visualization
 */

interface InventoryAnalyticsPageProps {
  selectedBranchId: string;
}

export default function InventoryAnalyticsPage({ selectedBranchId }: InventoryAnalyticsPageProps) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // Debug logging
  console.log('InventoryAnalyticsPage - selectedBranchId:', selectedBranchId);

  useInventoryRealtime(selectedBranchId);
  
  // Date range state
  const [dateRange, setDateRange] = useState<AnalyticsDateRange>(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30); // Last 30 days
    const range = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };
    console.log('Date range:', range);
    return range;
  });

  // Fetch data
  const { data: overview, isLoading: overviewLoading, error: overviewError } = useDashboardOverview(
    selectedBranchId,
    dateRange
  );
  const { data: financial, error: financialError } = useFinancialMetrics(selectedBranchId, dateRange);
  const { data: waste, error: wasteError } = useWasteMetrics(selectedBranchId, dateRange);
  const { data: inventory, error: inventoryError } = useInventoryMetrics(selectedBranchId, dateRange);
  const { data: usage, error: usageError } = useMaterialUsage(selectedBranchId, dateRange);
  const { data: recipeCosts, error: recipeCostsError } = useRecipeCostAnalysis(selectedBranchId);

  // Debug logging
  console.log('Analytics data:', { overview, financial, waste, inventory, usage, recipeCosts });
  console.log('Analytics errors:', { overviewError, financialError, wasteError, inventoryError, usageError, recipeCostsError });

  // Provide default values to prevent undefined errors
  const safeFinancial = financial || {
    revenue: 0,
    cogs: 0,
    netProfit: 0,
    netProfitMargin: 0,
    cogsRatio: 0,
    stockTurnoverRate: 0
  };

  const safeWaste = waste || {
    totalWasteCost: 0,
    wastePercentage: 0,
    wasteItems: []
  };

  const safeInventory = inventory || {
    totalInventoryValue: 0,
    totalMaterials: 0,
    outOfStockCount: 0,
    lowStockCount: 0,
    stockAlerts: []
  };

  const safeUsage = usage || [];

  const safeOverview = overview || {
    financial: safeFinancial,
    waste: safeWaste,
    inventory: safeInventory,
    usage: {
      topUsedMaterials: [],
      topCostlyMaterials: [],
      totalMaterialsUsed: 0
    }
  };

  if (!selectedBranchId) {
    return (
      <div className="p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {t('dashboard.inventory.analytics.no_branch_selected')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const handleDateRangeChange = (value: string) => {
    const endDate = new Date();
    const startDate = new Date();

    switch (value) {
      case 'last_7_days':
        startDate.setDate(startDate.getDate() - 7);
        break;
      case 'last_30_days':
        startDate.setDate(startDate.getDate() - 30);
        break;
      case 'last_90_days':
        startDate.setDate(startDate.getDate() - 90);
        break;
      case 'last_6_months':
        startDate.setMonth(startDate.getMonth() - 6);
        break;
      case 'last_year':
        startDate.setFullYear(startDate.getFullYear() - 1);
        break;
    }

    setDateRange({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });
  };

  const handleExport = () => {
    // Export functionality
    const dataToExport = {
      financial: safeFinancial,
      waste: safeWaste,
      inventory: safeInventory,
      usage: safeUsage,
      recipeCosts,
      dateRange
    };
    
    const blob = new Blob([JSON.stringify(dataToExport, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory-analytics-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex-1 space-y-4 md:space-y-6 p-3 md:p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 md:gap-4">
        <div>
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold tracking-tight">
            {t('dashboard.inventory.analytics.title')}
          </h1>
          <p className="text-xs md:text-sm text-muted-foreground mt-1">
            {t('dashboard.inventory.analytics.description')}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
          {/* Date Range Selector */}
          <Select onValueChange={handleDateRangeChange} defaultValue="last_30_days">
            <SelectTrigger className="w-full sm:w-56 text-sm md:text-base h-9 md:h-10">
              <Calendar className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1.5 md:mr-2 flex-shrink-0" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last_7_days" className="text-sm">
                {t('dashboard.inventory.analytics.time_range.last_7_days')}
              </SelectItem>
              <SelectItem value="last_30_days" className="text-sm">
                {t('dashboard.inventory.analytics.time_range.last_30_days')}
              </SelectItem>
              <SelectItem value="last_90_days" className="text-sm">
                {t('dashboard.inventory.analytics.time_range.last_90_days')}
              </SelectItem>
              <SelectItem value="last_6_months" className="text-sm">
                {t('dashboard.inventory.analytics.time_range.last_6_months')}
              </SelectItem>
              <SelectItem value="last_year" className="text-sm">
                {t('dashboard.inventory.analytics.time_range.last_year')}
              </SelectItem>
            </SelectContent>
          </Select>

          {/* Export Button */}
          <Button onClick={handleExport} variant="outline" className="gap-1.5 md:gap-2 text-sm md:text-base h-9 md:h-10 px-3 md:px-4">
            <Download className="h-3.5 w-3.5 md:h-4 md:w-4" />
            <span className="hidden sm:inline">{t('dashboard.inventory.analytics.export_button')}</span>
            <span className="sm:hidden">Export</span>
          </Button>
        </div>
      </div>

      {/* Stock Alerts */}
      {safeInventory.stockAlerts && safeInventory.stockAlerts.length > 0 && (
        <Alert variant={safeInventory.stockAlerts.some(a => a.severity === 'critical') ? 'destructive' : 'default'} className="border-0 md:border">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <AlertDescription>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
              <span className="font-medium text-sm">
                {t('dashboard.inventory.analytics.alerts.stock_alerts_title')}
              </span>
              <span className="text-xs sm:text-sm text-muted-foreground">
                {safeInventory.stockAlerts.length} {t('dashboard.inventory.analytics.alerts.items')}
              </span>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Main Tabs */}
      <Tabs defaultValue="overview" className="space-y-4 md:space-y-6">
        <div className="-mx-3 md:mx-0 px-3 md:px-0 overflow-x-auto hide-scrollbar">
          <TabsList className="inline-flex w-auto min-w-full md:min-w-0 h-auto p-1">
            <TabsTrigger value="overview" className="flex-shrink-0 gap-1.5 px-2 md:px-3 py-2 text-xs md:text-sm data-[state=active]:bg-background">
              <BarChart3 className="h-3.5 w-3.5 md:h-4 md:w-4" />
              <span className="hidden sm:inline">{t('dashboard.inventory.analytics.tabs.overview')}</span>
            </TabsTrigger>
            <TabsTrigger value="materials" className="flex-shrink-0 gap-1.5 px-2 md:px-3 py-2 text-xs md:text-sm data-[state=active]:bg-background">
              <Package className="h-3.5 w-3.5 md:h-4 md:w-4" />
              <span className="hidden sm:inline">Materials</span>
            </TabsTrigger>
            <TabsTrigger value="financial" className="flex-shrink-0 gap-1.5 px-2 md:px-3 py-2 text-xs md:text-sm data-[state=active]:bg-background">
              <DollarSign className="h-3.5 w-3.5 md:h-4 md:w-4" />
              <span className="hidden sm:inline">{t('dashboard.inventory.analytics.tabs.financial')}</span>
            </TabsTrigger>
            <TabsTrigger value="waste" className="flex-shrink-0 gap-1.5 px-2 md:px-3 py-2 text-xs md:text-sm data-[state=active]:bg-background">
              <Trash2 className="h-3.5 w-3.5 md:h-4 md:w-4" />
              <span className="hidden sm:inline">{t('dashboard.inventory.analytics.tabs.waste')}</span>
            </TabsTrigger>
            <TabsTrigger value="usage" className="flex-shrink-0 gap-1.5 px-2 md:px-3 py-2 text-xs md:text-sm data-[state=active]:bg-background">
              <Activity className="h-3.5 w-3.5 md:h-4 md:w-4" />
              <span className="hidden sm:inline">{t('dashboard.inventory.analytics.tabs.usage')}</span>
            </TabsTrigger>
            <TabsTrigger value="recipes" className="flex-shrink-0 gap-1.5 px-2 md:px-3 py-2 text-xs md:text-sm data-[state=active]:bg-background">
              <PieChart className="h-3.5 w-3.5 md:h-4 md:w-4" />
              <span className="hidden sm:inline">{t('dashboard.inventory.analytics.tabs.recipes')}</span>
            </TabsTrigger>
            <TabsTrigger value="advanced" className="flex-shrink-0 gap-1.5 px-2 md:px-3 py-2 text-xs md:text-sm data-[state=active]:bg-background">
              <ChefHat className="h-3.5 w-3.5 md:h-4 md:w-4" />
              <span className="hidden sm:inline">Advanced</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4 md:space-y-6 mt-0">
          {/* Info Alert when no revenue */}
          {safeFinancial.revenue === 0 && (
            <Alert className="bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription>
                <p className="font-medium text-blue-900">
                  📊 Getting Started: Your inventory system is set up and ready!
                </p>
                <p className="text-sm text-blue-800 mt-1">
                  You currently have <strong>{safeInventory.totalMaterials} materials</strong> with a total value of <strong>{formatCurrency(safeInventory.totalInventoryValue)}</strong>.
                  Revenue and profit metrics will appear here once you start processing orders.
                </p>
              </AlertDescription>
            </Alert>
          )}
          
          {/* Key Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3 lg:gap-4">
            <MetricCard
              title={t('dashboard.inventory.analytics.metrics.revenue')}
              value={formatCurrency(safeFinancial.revenue)}
              change={calculateChange(safeFinancial.revenue, 0)}
              trend={safeFinancial.revenue > 0 ? 'up' : 'neutral'}
              icon={DollarSign}
              iconColor="text-green-600"
              bgColor="bg-green-50"
            />
            <MetricCard
              title={t('dashboard.inventory.analytics.metrics.cogs')}
              value={formatCurrency(safeFinancial.cogs)}
              subtitle={`${safeFinancial.cogsRatio.toFixed(1)}% ${t('dashboard.inventory.analytics.metrics.of_revenue')}`}
              icon={ShoppingCart}
              iconColor="text-orange-600"
              bgColor="bg-orange-50"
            />
            <MetricCard
              title={t('dashboard.inventory.analytics.metrics.net_profit')}
              value={formatCurrency(safeFinancial.netProfit)}
              subtitle={`${safeFinancial.netProfitMargin.toFixed(1)}% ${t('dashboard.inventory.analytics.metrics.margin')}`}
              trend={safeFinancial.netProfit > 0 ? 'up' : 'down'}
              icon={TrendingUp}
              iconColor="text-blue-600"
              bgColor="bg-blue-50"
            />
            <MetricCard
              title={t('dashboard.inventory.analytics.metrics.inventory_value')}
              value={formatCurrency(safeInventory.totalInventoryValue)}
              subtitle={`${safeInventory.totalMaterials} ${t('dashboard.inventory.analytics.metrics.materials')}`}
              icon={Package}
              iconColor="text-purple-600"
              bgColor="bg-purple-50"
            />
          </div>

          {/* Secondary Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            <Card className="border-0 md:border shadow-sm overflow-hidden">
              <CardHeader className="pb-2 md:pb-3 p-3 md:p-4 lg:p-6">
                <CardTitle className="text-xs md:text-sm font-medium truncate">
                  {t('dashboard.inventory.analytics.metrics.stock_turnover')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
                <div className="text-xl md:text-2xl font-bold truncate">
                  {safeFinancial.stockTurnoverRate.toFixed(2)}x
                </div>
                <p className="text-[10px] md:text-xs text-muted-foreground mt-1 line-clamp-2">
                  {t('dashboard.inventory.analytics.metrics.stock_turnover_desc')}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 md:border shadow-sm overflow-hidden">
              <CardHeader className="pb-2 md:pb-3 p-3 md:p-4 lg:p-6">
                <CardTitle className="text-xs md:text-sm font-medium truncate">
                  {t('dashboard.inventory.analytics.metrics.waste_percentage')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
                <div className="text-xl md:text-2xl font-bold text-red-600 truncate">
                  {safeWaste.wastePercentage.toFixed(1)}%
                </div>
                <p className="text-[10px] md:text-xs text-muted-foreground mt-1 truncate">
                  {formatCurrency(safeWaste.totalWasteCost)} {t('dashboard.inventory.analytics.metrics.total_waste')}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 md:border shadow-sm overflow-hidden">
              <CardHeader className="pb-2 md:pb-3 p-3 md:p-4 lg:p-6">
                <CardTitle className="text-xs md:text-sm font-medium truncate">
                  {t('dashboard.inventory.analytics.metrics.stock_alerts')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
                <div className="flex items-center gap-2 md:gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-lg md:text-2xl font-bold text-red-600 truncate">
                      {safeInventory.outOfStockCount}
                    </div>
                    <p className="text-[10px] md:text-xs text-muted-foreground truncate">
                      {t('dashboard.inventory.analytics.metrics.out_of_stock')}
                    </p>
                  </div>
                  <Separator orientation="vertical" className="h-8 md:h-10" />
                  <div className="flex-1 min-w-0">
                    <div className="text-lg md:text-2xl font-bold text-orange-600 truncate">
                      {safeInventory.lowStockCount}
                    </div>
                    <p className="text-[10px] md:text-xs text-muted-foreground truncate">
                      {t('dashboard.inventory.analytics.metrics.low_stock')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Materials by Usage */}
          {safeOverview.usage.topUsedMaterials.length > 0 && (
            <Card className="border-0 md:border shadow-sm">
              <CardHeader className="p-3 md:p-4 lg:p-6">
                <CardTitle className="text-base md:text-lg">{t('dashboard.inventory.analytics.charts.top_used_materials')}</CardTitle>
                <CardDescription className="text-xs md:text-sm">
                  {t('dashboard.inventory.analytics.charts.top_used_materials_desc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
                <div className="space-y-4">
                  {safeOverview.usage.topUsedMaterials.map((material, index) => (
                    <div key={material.materialId} className="flex items-center gap-4">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{material.materialName}</p>
                        <p className="text-sm text-muted-foreground">
                          {material.usedQuantity.toFixed(2)} {material.unit}
                        </p>
                      </div>
                      <div className={`text-sm font-medium ${isRTL ? 'text-left' : 'text-right'}`}>
                        {formatCurrency(material.totalCost)}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Materials & Costs Tab */}
        <TabsContent value="materials" className="space-y-4 md:space-y-6 mt-0">
          <MaterialsCostTable branchId={selectedBranchId} />
        </TabsContent>

        {/* Financial Tab */}
        <TabsContent value="financial" className="space-y-4 md:space-y-6 mt-0">
          {/* Info Alert when no revenue */}
          {(!financial || financial.revenue === 0) && (
            <Alert className="border-0 md:border">
              <Info className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2 text-sm">
                  <p className="font-medium">No sales data available for the selected period</p>
                  <p className="text-xs md:text-sm">
                    Financial metrics like revenue, COGS, and profit are calculated from completed orders. 
                    To see these metrics:
                  </p>
                  <ul className="text-xs md:text-sm list-disc list-inside space-y-1 ml-2">
                    <li>Process orders through your POS system</li>
                    <li>Link menu items to recipes in the Recipes section</li>
                    <li>Ensure orders are marked as "completed" or "delivered"</li>
                  </ul>
                  <p className="text-xs md:text-sm font-medium mt-2">
                    💡 Tip: Check the "Materials & Costs" tab to view your current inventory value and material costs.
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          )}
          
          <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
            {/* Revenue Breakdown */}
            <Card className="border-0 md:border shadow-sm overflow-hidden">
              <CardHeader className="p-3 md:p-4 lg:p-6">
                <CardTitle className="text-base md:text-lg">{t('dashboard.inventory.analytics.financial.revenue_breakdown')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 md:space-y-4 p-3 md:p-4 lg:p-6 pt-0">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {t('dashboard.inventory.analytics.financial.total_revenue')}
                  </span>
                  <span className="text-lg font-bold">
                    {formatCurrency(financial?.revenue || 0)}
                  </span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {t('dashboard.inventory.analytics.financial.cogs')}
                  </span>
                  <span className="text-lg font-semibold text-orange-600">
                    -{formatCurrency(financial?.cogs || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {t('dashboard.inventory.analytics.financial.gross_profit')}
                  </span>
                  <span className="text-lg font-semibold text-green-600">
                    {formatCurrency(financial?.grossProfit || 0)}
                  </span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {t('dashboard.inventory.analytics.financial.expenses')}
                  </span>
                  <span className="text-lg font-semibold text-red-600">
                    -{formatCurrency(financial?.totalExpenses || 0)}
                  </span>
                </div>
                <Separator className="border-2" />
                <div className="flex items-center justify-between">
                  <span className="font-medium">
                    {t('dashboard.inventory.analytics.financial.net_profit')}
                  </span>
                  <span className="text-xl font-bold text-blue-600">
                    {formatCurrency(financial?.netProfit || 0)}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Profit Margins */}
            <Card className="border-0 md:border shadow-sm overflow-hidden">
              <CardHeader className="p-3 md:p-4 lg:p-6">
                <CardTitle className="text-base md:text-lg">{t('dashboard.inventory.analytics.financial.profit_margins')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 md:space-y-6 p-3 md:p-4 lg:p-6 pt-0">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">
                      {t('dashboard.inventory.analytics.financial.gross_margin')}
                    </span>
                    <span className="text-lg font-bold">
                      {financial?.grossProfitMargin?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 transition-all"
                      style={{ width: `${Math.min(100, financial?.grossProfitMargin || 0)}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('dashboard.inventory.analytics.financial.gross_margin_desc')}
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">
                      {t('dashboard.inventory.analytics.financial.net_margin')}
                    </span>
                    <span className="text-lg font-bold">
                      {financial?.netProfitMargin?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all"
                      style={{ width: `${Math.min(100, financial?.netProfitMargin || 0)}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('dashboard.inventory.analytics.financial.net_margin_desc')}
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">
                      {t('dashboard.inventory.analytics.financial.cogs_ratio')}
                    </span>
                    <span className="text-lg font-bold">
                      {financial?.cogsRatio?.toFixed(1) || 0}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-orange-500 transition-all"
                      style={{ width: `${Math.min(100, financial?.cogsRatio || 0)}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('dashboard.inventory.analytics.financial.cogs_ratio_desc')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Key Financial Insights */}
          <Card className="border-0 md:border shadow-sm">
            <CardHeader className="p-3 md:p-4 lg:p-6">
              <CardTitle className="text-base md:text-lg">{t('dashboard.inventory.analytics.key_insights.title')}</CardTitle>
            </CardHeader>
            <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
              <div className="grid gap-3 md:gap-4 grid-cols-1 md:grid-cols-2">
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    {t('dashboard.inventory.analytics.key_insights.positive_indicators')}
                  </h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {financial?.grossProfitMargin && financial.grossProfitMargin > 50 && (
                      <li className="flex items-start gap-2">
                        <span className="text-green-600">•</span>
                        {t('dashboard.inventory.analytics.insights.healthy_gross_margin')}
                      </li>
                    )}
                    {financial?.stockTurnoverRate && financial.stockTurnoverRate > 4 && (
                      <li className="flex items-start gap-2">
                        <span className="text-green-600">•</span>
                        {t('dashboard.inventory.analytics.insights.good_stock_turnover')}
                      </li>
                    )}
                    {financial?.netProfit && financial.netProfit > 0 && (
                      <li className="flex items-start gap-2">
                        <span className="text-green-600">•</span>
                        {t('dashboard.inventory.analytics.insights.profitable_period')}
                      </li>
                    )}
                  </ul>
                </div>
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-orange-600" />
                    {t('dashboard.inventory.analytics.key_insights.areas_for_improvement')}
                  </h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {financial?.cogsRatio && financial.cogsRatio > 35 && (
                      <li className="flex items-start gap-2">
                        <span className="text-orange-600">•</span>
                        {t('dashboard.inventory.analytics.insights.high_cogs')}
                      </li>
                    )}
                    {waste?.wastePercentage && waste.wastePercentage > 5 && (
                      <li className="flex items-start gap-2">
                        <span className="text-orange-600">•</span>
                        {t('dashboard.inventory.analytics.insights.high_waste')}
                      </li>
                    )}
                    {inventory?.lowStockCount && inventory.lowStockCount > 5 && (
                      <li className="flex items-start gap-2">
                        <span className="text-orange-600">•</span>
                        {t('dashboard.inventory.analytics.insights.many_low_stock')}
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Waste Tab */}
        <TabsContent value="waste" className="space-y-4 md:space-y-6 mt-0">
          {/* Waste Overview */}
          <div className="grid gap-3 md:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            <Card className="border-0 md:border shadow-sm overflow-hidden">
              <CardHeader className="pb-2 md:pb-3 p-3 md:p-4 lg:p-6">
                <CardTitle className="text-xs md:text-sm font-medium truncate">
                  {t('dashboard.inventory.analytics.waste.total_waste')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
                <div className="text-xl md:text-2xl font-bold text-red-600 truncate">
                  {waste?.totalWaste?.toFixed(2) || '0.00'}
                </div>
                <p className="text-[10px] md:text-xs text-muted-foreground mt-1 truncate">
                  {t('dashboard.inventory.analytics.waste.total_units')}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 md:border shadow-sm overflow-hidden">
              <CardHeader className="pb-2 md:pb-3 p-3 md:p-4 lg:p-6">
                <CardTitle className="text-xs md:text-sm font-medium truncate">
                  {t('dashboard.inventory.analytics.waste.waste_cost')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
                <div className="text-xl md:text-2xl font-bold text-red-600 truncate">
                  {formatCurrency(waste?.totalWasteCost || 0)}
                </div>
                <p className="text-[10px] md:text-xs text-muted-foreground mt-1 truncate">
                  {waste?.wastePercentage?.toFixed(1) || 0}% {t('dashboard.inventory.analytics.waste.of_purchases')}
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 md:border shadow-sm overflow-hidden">
              <CardHeader className="pb-2 md:pb-3 p-3 md:p-4 lg:p-6">
                <CardTitle className="text-xs md:text-sm font-medium truncate">
                  {t('dashboard.inventory.analytics.waste.significant_waste_items')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
                <div className="text-xl md:text-2xl font-bold text-orange-600">
                  {waste?.significantWaste?.length || 0}
                </div>
                <p className="text-[10px] md:text-xs text-muted-foreground mt-1 truncate">
                  {t('dashboard.inventory.analytics.waste.items_over_threshold')}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Significant Waste Items */}
          {waste?.significantWaste && waste.significantWaste.length > 0 && (
            <Card className="border-0 md:border shadow-sm">
              <CardHeader className="p-3 md:p-4 lg:p-6">
                <CardTitle className="text-base md:text-lg">{t('dashboard.inventory.analytics.waste.high_waste_materials')}</CardTitle>
                <CardDescription className="text-xs md:text-sm">
                  {t('dashboard.inventory.analytics.waste.high_waste_desc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {waste.significantWaste.map((item) => (
                    <div key={item.material_id} className="flex items-center gap-4 p-3 rounded-lg bg-red-50 border border-red-100">
                      <Trash2 className="h-5 w-5 text-red-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{item.material_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {item.waste_amount.toFixed(2)} • {item.waste_percentage.toFixed(1)}% {t('dashboard.inventory.analytics.waste.waste_rate')}
                        </p>
                      </div>
                      <div className={`text-right ${isRTL ? 'text-left' : ''}`}>
                        <p className="text-lg font-bold text-red-600">
                          {formatCurrency(item.waste_cost)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recommendations */}
          {waste?.recommendations && waste.recommendations.length > 0 && (
            <Card className="border-0 md:border shadow-sm">
              <CardHeader className="p-3 md:p-4 lg:p-6">
                <CardTitle className="text-base md:text-lg">{t('dashboard.inventory.analytics.waste.recommendations')}</CardTitle>
              </CardHeader>
              <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
                <div className="space-y-2 md:space-y-3">
                  {waste.recommendations.map((rec, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-100">
                      <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm">{rec}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Usage Tab */}
        <TabsContent value="usage" className="space-y-4 md:space-y-6 mt-0">
          {usage && usage.length > 0 ? (
            <Card className="border-0 md:border shadow-sm">
              <CardHeader className="p-3 md:p-4 lg:p-6">
                <CardTitle className="text-base md:text-lg">{t('dashboard.inventory.analytics.usage.material_consumption')}</CardTitle>
                <CardDescription className="text-xs md:text-sm">
                  {t('dashboard.inventory.analytics.usage.material_consumption_desc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
                <div className="space-y-2 md:space-y-3">
                  {usage.map((material) => (
                    <div key={material.materialId} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-2 md:p-3 rounded-lg border hover:bg-accent/50 transition-colors">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm md:text-base truncate">{material.materialName}</p>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] md:text-xs text-muted-foreground mt-1">
                          <span className="whitespace-nowrap">
                            Used: {material.usedQuantity.toFixed(2)} {material.unit}
                          </span>
                          <span className="hidden sm:inline">•</span>
                          <span className="whitespace-nowrap">
                            Stock: {material.currentStock.toFixed(2)} {material.unit}
                          </span>
                          <span className="hidden sm:inline">•</span>
                          <span className="whitespace-nowrap">
                            Avg: {material.avgDailyUsage.toFixed(2)}/day
                          </span>
                        </div>
                      </div>
                      <div className={`${isRTL ? 'text-left sm:text-right' : 'text-left sm:text-right'} flex-shrink-0`}>
                        <p className="font-semibold text-sm md:text-base">{formatCurrency(material.totalCost)}</p>
                        <p className="text-[10px] md:text-xs text-muted-foreground truncate">
                          {material.daysUntilRestock < 999
                            ? `${material.daysUntilRestock} days left`
                            : 'Well stocked'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 md:border shadow-sm">
              <CardContent className="text-center py-8 md:py-12 px-4">
                <Activity className="h-12 w-12 md:h-16 md:w-16 text-muted-foreground/50 mx-auto mb-3 md:mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {t('dashboard.inventory.analytics.usage.no_data')}
                </h3>
                <p className="text-muted-foreground">
                  {t('dashboard.inventory.analytics.usage.no_data_desc')}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Recipes Tab */}
        <TabsContent value="recipes" className="space-y-4 md:space-y-6 mt-0">
          {recipeCosts && recipeCosts.length > 0 ? (
            <Card className="border-0 md:border shadow-sm">
              <CardHeader className="p-3 md:p-4 lg:p-6">
                <CardTitle className="text-base md:text-lg">{t('dashboard.inventory.analytics.recipes.cost_analysis')}</CardTitle>
                <CardDescription className="text-xs md:text-sm">
                  {t('dashboard.inventory.analytics.recipes.cost_analysis_desc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
                <div className="space-y-2 md:space-y-3">
                  {recipeCosts.map((recipe) => (
                    <div key={recipe.recipeId} className="p-4 rounded-lg border hover:bg-accent/50 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold">{recipe.recipeName}</h4>
                          <p className="text-sm text-muted-foreground">
                            {recipe.ingredientCosts.length} {t('dashboard.inventory.analytics.recipes.ingredients')}
                          </p>
                        </div>
                        <div className={`text-right ${isRTL ? 'text-left' : ''} flex-shrink-0 ml-4`}>
                          <p className="text-lg font-bold">{formatCurrency(recipe.totalCost)}</p>
                          {recipe.sellingPrice && (
                            <p className={`text-sm font-medium ${recipe.profitMargin > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {recipe.profitMargin.toFixed(1)}% {t('dashboard.inventory.analytics.recipes.margin')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs md:text-sm">
                        <div>
                          <span className="text-muted-foreground">{t('dashboard.inventory.analytics.recipes.cost_per_serving')}:</span>
                          <span className={`font-medium ${isRTL ? 'mr-2' : 'ml-2'}`}>
                            {formatCurrency(recipe.costPerServing)}
                          </span>
                        </div>
                        {recipe.sellingPrice && (
                          <div>
                            <span className="text-muted-foreground">{t('dashboard.inventory.analytics.recipes.selling_price')}:</span>
                            <span className={`font-medium ${isRTL ? 'mr-2' : 'ml-2'}`}>
                              {formatCurrency(recipe.sellingPrice)}
                            </span>
                          </div>
                        )}
                        <div>
                          <span className="text-muted-foreground">{t('dashboard.inventory.analytics.recipes.yield')}:</span>
                          <span className={`font-medium ${isRTL ? 'mr-2' : 'ml-2'}`}>
                            {recipe.yield}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-0 md:border shadow-sm">
              <CardContent className="text-center py-8 md:py-12 px-4">
                <ChefHat className="h-12 w-12 md:h-16 md:w-16 text-muted-foreground/50 mx-auto mb-3 md:mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {t('dashboard.inventory.analytics.recipes.no_data')}
                </h3>
                <p className="text-muted-foreground">
                  {t('dashboard.inventory.analytics.recipes.no_data_desc')}
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Advanced Analytics Tab */}
        <TabsContent value="advanced" className="space-y-4 md:space-y-6 mt-0">
          <AdvancedAnalyticsTables selectedBranchId={selectedBranchId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ===== HELPER COMPONENTS =====

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  change?: number;
  trend?: 'up' | 'down' | 'neutral';
  icon: React.ElementType;
  iconColor: string;
  bgColor: string;
}

function MetricCard({
  title,
  value,
  subtitle,
  change,
  trend,
  icon: Icon,
  iconColor,
  bgColor
}: MetricCardProps) {
  return (
    <Card className="border-0 md:border shadow-sm overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between pb-1.5 md:pb-2 p-3 md:p-4 lg:p-6">
        <CardTitle className="text-xs md:text-sm font-medium truncate flex-1 min-w-0 pr-2">{title}</CardTitle>
        <div className={`p-1.5 md:p-2 rounded-lg ${bgColor} flex-shrink-0`}>
          <Icon className={`h-3 w-3 md:h-4 md:w-4 ${iconColor}`} />
        </div>
      </CardHeader>
      <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
        <div className="text-base md:text-xl lg:text-2xl font-bold truncate">{value}</div>
        {subtitle && (
          <p className="text-[10px] md:text-xs text-muted-foreground mt-1 truncate">{subtitle}</p>
        )}
        {change !== undefined && trend && (
          <div className="flex items-center gap-1 mt-1.5 md:mt-2">
            {trend === 'up' ? (
              <ArrowUpRight className="h-3 w-3 md:h-4 md:w-4 text-green-600 flex-shrink-0" />
            ) : trend === 'down' ? (
              <ArrowDownRight className="h-3 w-3 md:h-4 md:w-4 text-red-600 flex-shrink-0" />
            ) : null}
            <span className={`text-xs md:text-sm font-medium truncate ${
              trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-gray-600'
            }`}>
              {change > 0 ? '+' : ''}{change}%
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ===== HELPER FUNCTIONS =====

function formatCurrency(value: number): string {
  return `EGP ${value.toFixed(2)}`;
}

function calculateChange(current: number | undefined, previous: number): number {
  if (!current || previous === 0) return 0;
  return ((current - previous) / previous) * 100;
}

// ===== MATERIALS COST TABLE COMPONENT =====

interface MaterialsCostTableProps {
  branchId: string;
}

function MaterialsCostTable({ branchId }: MaterialsCostTableProps) {
  const { t } = useTranslation();
  const { data: materials, isLoading, error } = useMaterials(branchId);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <Activity className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading materials...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <AlertCircle className="h-16 w-16 text-red-500/50 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Error Loading Materials</h3>
          <p className="text-muted-foreground">{error.message}</p>
        </CardContent>
      </Card>
    );
  }

  if (!materials || materials.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <Package className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Materials Yet</h3>
          <p className="text-muted-foreground">
            Add materials to your inventory to see cost breakdown
          </p>
        </CardContent>
      </Card>
    );
  }

  // Calculate summary metrics
  const totalInventoryValue = materials.reduce(
    (sum, m) => sum + (m.current_stock * m.cost_per_unit),
    0
  );
  const totalMaterials = materials.length;
  const averageCostPerUnit = materials.reduce((sum, m) => sum + m.cost_per_unit, 0) / totalMaterials;
  const lowStockItems = materials.filter(m => m.reorder_point && m.current_stock <= m.reorder_point).length;
  const outOfStockItems = materials.filter(m => m.current_stock <= 0).length;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-3 md:gap-4 grid-cols-2 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-0 md:border shadow-sm overflow-hidden">
          <CardHeader className="pb-2 md:pb-3 p-3 md:p-4 lg:p-6">
            <CardTitle className="text-xs md:text-sm font-medium truncate">Total Inventory Value</CardTitle>
          </CardHeader>
          <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
            <div className="text-lg md:text-2xl font-bold truncate">{formatCurrency(totalInventoryValue)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {totalMaterials} materials in stock
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 md:border shadow-sm overflow-hidden">
          <CardHeader className="pb-2 md:pb-3 p-3 md:p-4 lg:p-6">
            <CardTitle className="text-xs md:text-sm font-medium truncate">Average Cost Per Unit</CardTitle>
          </CardHeader>
          <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
            <div className="text-lg md:text-2xl font-bold truncate">{formatCurrency(averageCostPerUnit)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Across all materials
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 md:border shadow-sm overflow-hidden">
          <CardHeader className="pb-2 md:pb-3 p-3 md:p-4 lg:p-6">
            <CardTitle className="text-xs md:text-sm font-medium truncate">Low Stock Alerts</CardTitle>
          </CardHeader>
          <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
            <div className="text-lg md:text-2xl font-bold text-orange-600 truncate">{lowStockItems}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Need reordering soon
            </p>
          </CardContent>
        </Card>

        <Card className="border-0 md:border shadow-sm overflow-hidden">
          <CardHeader className="pb-2 md:pb-3 p-3 md:p-4 lg:p-6">
            <CardTitle className="text-xs md:text-sm font-medium truncate">Out of Stock</CardTitle>
          </CardHeader>
          <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
            <div className="text-lg md:text-2xl font-bold text-red-600 truncate">{outOfStockItems}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Require immediate attention
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Materials Table */}
      <Card className="border-0 md:border shadow-sm">
        <CardHeader className="p-3 md:p-4 lg:p-6">
          <CardTitle className="text-base md:text-lg">Materials Cost Breakdown</CardTitle>
          <CardDescription className="text-xs md:text-sm">
            Detailed view of all materials with costs, quantities, and total values
          </CardDescription>
        </CardHeader>
        <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Material Name</TableHead>
                  <TableHead className="font-semibold text-right">Cost Per Unit</TableHead>
                  <TableHead className="font-semibold text-right">Current Stock</TableHead>
                  <TableHead className="font-semibold text-right">Unit</TableHead>
                  <TableHead className="font-semibold text-right">Total Value</TableHead>
                  <TableHead className="font-semibold text-right">Reorder Point</TableHead>
                  <TableHead className="font-semibold text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {materials
                  .sort((a, b) => (b.current_stock * b.cost_per_unit) - (a.current_stock * a.cost_per_unit))
                  .map((material) => {
                    const totalValue = material.current_stock * material.cost_per_unit;
                    const isOutOfStock = material.current_stock <= 0;
                    const isLowStock = !isOutOfStock && material.reorder_point && material.current_stock <= material.reorder_point;
                    
                    return (
                      <TableRow key={material.id}>
                        <TableCell className="font-medium">{material.name}</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(material.cost_per_unit)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {material.current_stock.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {material.unit_type}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(totalValue)}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {material.reorder_point ? material.reorder_point.toFixed(2) : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {isOutOfStock ? (
                            <Badge variant="destructive" className="gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              Out of Stock
                            </Badge>
                          ) : isLowStock ? (
                            <Badge variant="secondary" className="gap-1 bg-orange-100 text-orange-700 border-orange-300">
                              <AlertCircle className="h-3 w-3" />
                              Low Stock
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="gap-1 bg-green-100 text-green-700 border-green-300">
                              <CheckCircle className="h-3 w-3" />
                              In Stock
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Additional Info */}
      <Card className="border-0 md:border shadow-sm">
        <CardHeader className="p-3 md:p-4 lg:p-6">
          <CardTitle className="text-base md:text-lg">Inventory Insights</CardTitle>
        </CardHeader>
        <CardContent className="p-3 md:p-4 lg:p-6 pt-0">
          <div className="grid gap-3 md:gap-4 grid-cols-1 md:grid-cols-2">
            <div className="space-y-2">
              <h4 className="font-medium text-green-600 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Healthy Indicators
              </h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Total inventory value: {formatCurrency(totalInventoryValue)}</li>
                <li>• {totalMaterials - outOfStockItems - lowStockItems} materials in good stock</li>
                {totalInventoryValue > 0 && (
                  <li>• Average material value: {formatCurrency(totalInventoryValue / totalMaterials)}</li>
                )}
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-orange-600 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Attention Needed
              </h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                {outOfStockItems > 0 && (
                  <li>• {outOfStockItems} materials out of stock</li>
                )}
                {lowStockItems > 0 && (
                  <li>• {lowStockItems} materials running low</li>
                )}
                {outOfStockItems === 0 && lowStockItems === 0 && (
                  <li>• No immediate stock issues detected</li>
                )}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

